import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Bell, PushPin, Trophy, Coins, ArrowRight, Sparkle } from "@phosphor-icons/react";

/**
 * /signals — the full Signals feed page.
 *
 * Visual redesign (iter42.9):
 *   • Each card shows a hero band (emoji or image), title, one-line summary,
 *     and a "Read →" affordance instead of dumping the full body inline.
 *   • Tapping a card opens `/signals/:sid` for the full formatted view.
 *   • Filter chips + pinned hoisting preserved from the previous version.
 */
const FILTERS = [
  { id: "all",         label: "All" },
  { id: "unread",      label: "Unread" },
  { id: "update",      label: "Updates" },
  { id: "announcement",label: "News" },
  { id: "championship",label: "Championship" },
];

const TYPE_ICONS = {
  announcement: Bell, championship: Trophy, prize: Coins,
  update: Sparkle, event: Bell, mode: Bell,
};

// One accent-gradient per signal type for the hero band when no image / emoji.
const TYPE_GRADIENT = {
  announcement: "from-nk-gold/25 via-nk-gold/8 to-transparent",
  update:       "from-emerald-500/25 via-emerald-500/8 to-transparent",
  event:        "from-sky-500/25 via-sky-500/8 to-transparent",
  championship: "from-amber-500/35 via-amber-500/10 to-transparent",
  mode:         "from-violet-500/25 via-violet-500/8 to-transparent",
  prize:        "from-nk-gold/35 via-nk-gold/10 to-transparent",
};

export default function SignalsPage() {
  const [feed, setFeed] = useState(null);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api.get("/signals?limit=100").then((r) => setFeed(r.data)).catch(() => setFeed({ items: [], unread_count: 0 }));
  }, []);

  const markAll = async () => {
    try {
      await api.post("/signals/mark-all-read");
      setFeed((f) => f ? {
        ...f,
        items: f.items.map((it) => ({ ...it, read: true })),
        unread_count: 0,
      } : f);
    } catch { /* ignore */ }
  };

  if (!feed) return <div className="p-6 text-white/45 text-sm">Loading…</div>;

  const visible = feed.items.filter((it) => {
    if (filter === "all") return true;
    if (filter === "unread") return !it.read;
    return it.signal_type === filter;
  });

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6 space-y-5" data-testid="signals-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="eyebrow mb-1">The league feed</p>
          <h1 className="font-display text-3xl sm:text-4xl font-medium text-white">Signals</h1>
          <p className="text-sm text-white/55 mt-1">
            Championships, mode updates, and league announcements — all in one place.
          </p>
        </div>
        <button
          onClick={markAll}
          disabled={feed.unread_count === 0}
          data-testid="signals-page-mark-all"
          className="text-[10px] uppercase tracking-widest text-nk-gold hover:text-nk-gold/80 disabled:text-white/25 disabled:cursor-not-allowed shrink-0"
        >
          Mark all read
        </button>
      </div>

      {/* Filter chips */}
      <div className="flex items-center gap-1.5 overflow-x-auto pb-1 -mx-1 px-1">
        {FILTERS.map((f) => {
          const active = filter === f.id;
          return (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              data-testid={`signals-filter-${f.id}`}
              className={`px-3 py-1.5 rounded-full text-[11px] uppercase tracking-widest whitespace-nowrap transition-colors ${
                active
                  ? "bg-nk-gold text-nk-charcoal"
                  : "border border-white/12 text-white/70 hover:border-nk-gold/40 hover:text-nk-gold"
              }`}
            >{f.label}</button>
          );
        })}
      </div>

      {/* Feed */}
      {visible.length === 0 ? (
        <div className="nk-card p-10 text-center" data-testid="signals-empty">
          <Bell size={36} weight="duotone" className="text-white/25 mx-auto mb-3" />
          <p className="text-white/70">No signals in this view</p>
          <p className="text-xs text-white/45 mt-1">Try switching the filter, or check back after the next league event.</p>
        </div>
      ) : (
        <div className="grid gap-4" data-testid="signals-list">
          {visible.map((it) => <SignalCard key={it.signal_id} item={it} />)}
        </div>
      )}
    </div>
  );
}

function SignalCard({ item }) {
  const Icon = TYPE_ICONS[item.signal_type] || Bell;
  const gradient = TYPE_GRADIENT[item.signal_type] || TYPE_GRADIENT.update;
  const isAuto = item.signal_id?.startsWith?.("auto_");
  const detailHref = isAuto ? (item.cta_href || "/signals") : `/signals/${item.signal_id}`;

  return (
    <Link
      to={detailHref}
      data-testid={`signal-card-${item.signal_id}`}
      className={`group nk-card overflow-hidden block transition-colors hover:border-nk-gold/40 ${
        item.read ? "" : "border-nk-gold/25"
      }`}
    >
      {/* Hero band — image OR gradient with hero_emoji */}
      {item.image_data_url ? (
        <div className="relative w-full aspect-[16/9] bg-black/25 overflow-hidden">
          {/* eslint-disable-next-line */}
          <img src={item.image_data_url} alt="" className="w-full h-full object-cover" />
          {item.pinned && <PinBadge />}
        </div>
      ) : (
        <div className={`relative aspect-[16/6] bg-gradient-to-br ${gradient} flex items-center justify-center overflow-hidden`}>
          <span className="font-display text-5xl sm:text-6xl select-none" aria-hidden>
            {item.hero_emoji || "✨"}
          </span>
          {item.pinned && <PinBadge />}
          {/* Type + read state chip */}
          <div className="absolute bottom-3 left-4 flex items-center gap-2">
            <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest text-white/70 bg-black/35 backdrop-blur px-2 py-0.5 rounded-full">
              <Icon size={9} weight="fill" />
              {item.signal_type}
            </span>
            {!item.read && (
              <span className="text-[9px] uppercase tracking-widest text-nk-charcoal bg-nk-gold px-2 py-0.5 rounded-full font-medium">
                new
              </span>
            )}
          </div>
        </div>
      )}

      {/* Content */}
      <div className="p-5">
        <h3 className={`font-display text-lg sm:text-xl font-medium leading-snug mb-1.5 ${
          item.read ? "text-white/85" : "text-white"
        }`}>{item.title}</h3>

        <p className={`text-sm leading-relaxed ${
          item.read ? "text-white/55" : "text-white/70"
        }`}>
          {item.summary || truncate(item.body, 180)}
        </p>

        <div className="flex items-center justify-between mt-4">
          <span className="text-[10px] text-white/40 uppercase tracking-wider font-mono">
            {formatWhen(item.published_at)}
          </span>
          <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest text-nk-gold group-hover:translate-x-0.5 transition-transform">
            Read <ArrowRight size={11} weight="bold" />
          </span>
        </div>
      </div>
    </Link>
  );
}

function PinBadge() {
  return (
    <div className="absolute top-3 right-3 inline-flex items-center gap-1 text-[9px] uppercase tracking-widest text-nk-charcoal bg-nk-gold px-2 py-1 rounded-full font-medium">
      <PushPin size={9} weight="fill" /> Pinned
    </div>
  );
}

function truncate(text, n = 180) {
  if (!text) return "";
  const t = text.replace(/^──[^─]+──\s*/gm, "").replace(/\n{2,}/g, " ").replace(/\s{2,}/g, " ").trim();
  return t.length > n ? t.slice(0, n).trim() + "…" : t;
}

function formatWhen(iso) {
  if (!iso) return "";
  try {
    const t = new Date(iso);
    const mins = Math.round((Date.now() - t) / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    if (mins < 60 * 24) return `${Math.round(mins / 60)}h ago`;
    if (mins < 60 * 24 * 7) return `${Math.round(mins / (60 * 24))}d ago`;
    return t.toLocaleDateString();
  } catch { return iso; }
}
